import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import { Typography, WithStyles } from "@material-ui/core";
import { styles } from "./ManageSubscriptionContactsView.styles";
import SubscriptionContactModel from "../../../models/subscription/SubscriptionContactModel";
import SubscriptionModel from "../../../models/subscription/SubscriptionModel";
import * as SubscriptionAction from "../../../stores/subscriptions/SubscriptionAction";
import UniversalTable from "../../components/UniversalTable/UniversalTable";
import { IUniversalTableColumn, IUniversalTableData } from "../../components/UniversalTable/interfaces";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../constants/ToastStatusEnum";
import { useTranslation } from "react-i18next";
import { selectRequesting } from "../../../selectors/requesting/RequestingSelector";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { useParams } from "react-router-dom";
import { CsvExportButton } from "../../components/CsvExport/CsvExportButton";
import { today } from "../../../utilities/dateTimeUtils";
import { isEmailValid } from "../../../utilities/generalUtils";
import { CenteredContainer } from "../../components/CenteredContainer/CenteredContainer";

export interface IManageSubscriptionsViewProps extends WithStyles<typeof styles> {}

const ManageSubscriptionContactsViewPure: React.FunctionComponent<IManageSubscriptionsViewProps> = ({
    classes
}: IManageSubscriptionsViewProps) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const { subscriptionId } = useParams();

    useEffect(() => {
        dispatch(SubscriptionAction.requestSubscriptionContacts(subscriptionId));
        dispatch(SubscriptionAction.requestAllSubscriptionTypes());
    }, [subscriptionId, dispatch]);

    const subscriptionsFromState = useSelector(state => state.subscriptions);
    const isRequesting = useSelector(state =>
        selectRequesting(state, [
            SubscriptionAction.REQUEST_ALL_SUBSCRIPTIONS,
            SubscriptionAction.REQUEST_SUBSCRIPTION_CONTACTS
        ])
    );

    let contacts: IUniversalTableData;
    if (subscriptionsFromState?.contacts) {
        contacts = Object.values(subscriptionsFromState?.contacts)?.map((contact: SubscriptionContactModel) => ({
            id: contact.id,
            email: contact.email
        }));
    }

    let currentSubscription: any;
    if (subscriptionsFromState?.allSubscriptions) {
        currentSubscription = Object.values(subscriptionsFromState?.allSubscriptions)?.find(
            (subscription: SubscriptionModel) => subscription.id === parseInt(subscriptionId)
        );
    }

    const subscriptionName = currentSubscription ? currentSubscription?.name : "";
    const subscriptionFullTitle = currentSubscription
        ? `${currentSubscription?.name} - ${currentSubscription?.description}`
        : "";

    const handleEditFinished = (index, newEmail) => {
        dispatch(SubscriptionAction.updateSubscriptionContact(subscriptionId, contacts[index].id, newEmail)).then(
            () => {
                enqueueSnackbar(t("admin.manageSubscriptionContactsView.contactEdited"), {
                    variant: ToastStatusEnum.Info
                });
                dispatch(SubscriptionAction.requestSubscriptionContacts(subscriptionId));
            }
        );
    };

    const handleDeleteFinished = index => {
        dispatch(SubscriptionAction.deleteSubscriptionContact(subscriptionId, contacts[index].id)).then(() => {
            enqueueSnackbar(t("admin.manageSubscriptionContactsView.contactDeleted"), {
                variant: ToastStatusEnum.Info
            });
            dispatch(SubscriptionAction.requestSubscriptionContacts(subscriptionId));
        });
    };

    const handleAddFinished = newEmail => {
        dispatch(SubscriptionAction.addSubscriptionContact(subscriptionId, newEmail)).then(() => {
            enqueueSnackbar(t("admin.manageSubscriptionContactsView.contactAdded"), {
                variant: ToastStatusEnum.Info
            });
            dispatch(SubscriptionAction.requestSubscriptionContacts(subscriptionId));
        });
    };

    const columns: IUniversalTableColumn[] = [
        {
            type: "text",
            title: t("general.email"),
            field: "email",
            validate: rowData => {
                return isEmailValid(rowData?.email)
                    ? true
                    : { isValid: false, helperText: t("admin.manageSubscriptionContactsView.invalidEmail") };
            }
        },
        {
            type: "actions",
            title: t("admin.taskView.actions"),
            actions: [
                {
                    name: "edit",
                    tooltip: t("general.edit"),
                    onEditFinished: (index, newValue) => handleEditFinished(index, newValue)
                },
                {
                    name: "delete",
                    tooltip: t("general.delete"),
                    onDeleteFinished: index => handleDeleteFinished(index),
                    deleteQuestion: t("admin.manageSubscriptionContactsView.deleteQuestion")
                },
                {
                    name: "add",
                    tooltip: t("admin.manageSubscriptionsView.addSubscription"),
                    onAddFinished: newValue => handleAddFinished(newValue)
                }
            ]
        }
    ];

    const contacts_mapping = [
        {
            label: "Email",
            key: "email"
        }
    ];

    return (
        <CenteredContainer>
            <Breadcrumbs
                items={[
                    { title: t("admin.adminPanel"), href: "/admin" },
                    {
                        title: t("admin.manageSubscriptionsView.manageSubscriptions"),
                        href: "/admin/manage-subscriptions"
                    },
                    {
                        title: `${t(
                            "admin.manageSubscriptionContactsView.manageSubscriptionContacts"
                        )} - ${subscriptionName}`
                    }
                ]}
            />
            <div className={classes.title}>
                <Typography variant="h4">{subscriptionFullTitle}</Typography>
                <Typography variant="h5">{t("general.contactList")}</Typography>
            </div>
            <div className={classes.exportButton}>
                <CsvExportButton
                    data={contacts ? contacts : []}
                    outputFilename={`${subscriptionName}_contacts_${today().format("YYYYMMDD")}.csv`}
                    mappings={contacts_mapping}
                >
                    {t("admin.manageSubscriptionContactsView.exportContacts")}
                </CsvExportButton>
            </div>
            <UniversalTable columns={columns} data={contacts} isLoading={isRequesting} searchable />
        </CenteredContainer>
    );
};

export const ManageSubscriptionContactsView = withStyles(styles)(ManageSubscriptionContactsViewPure);
